// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Every call hits local SQLite via Tauri IPC (durable, offline, on-disk).
//
// Data model (per rebuild spec): journal entries { title, mood 1-5, timestamp(created_at),
// body }. Privacy is PULL-NOT-PUSH: the weekly reflection + ambient chat context receive only
// title/mood/timestamp METADATA; the body text transits ONLY on an explicit recap ask
// (chat `recap` action — sent once for that answer, Oriora retains nothing).

import { invoke } from "./tauri-web";

export interface JournalEntry {
  id: string;
  title: string;
  body: string; // stored locally; sent ONLY on an explicit chat `recap` ask, never ambiently
  mood: number; // 1-5
  created_at: string; // ISO timestamp
  updated_at: string;
  photo_ref: string; // on-device photo: downscaled JPEG data-URL (iOS) / path (desktop); "" = none. Never sent to the brain.
  tags: string;      // comma-joined, normalised on write. "" = none.
  place: string;     // "lat,lon,weather" — ~1km precision, device-only. "" = none.
}

export interface NewEntry {
  title: string;
  body: string;
  mood: number;
  // optional explicit timestamp (defaults to now on the Rust side)
  created_at?: string;
  photo_ref?: string;
  tags?: string;
  place?: string;
}

export type EntryPatch = Partial<{
  title: string;
  body: string;
  mood: number;
  photo_ref: string;
  tags: string;
  place: string;
}>;

/** The human half of a stored place ("51.5,-0.12,cloudy 18°" → "cloudy 18°"). */
export const placeWeather = (place: string): string => {
  const parts = place.split(",");
  return parts.length >= 3 ? parts.slice(2).join(",").trim() : "";
};

/** The coordinates, when present — what a future map view would read. */
export const placeCoords = (place: string): { lat: number; lon: number } | null => {
  const [a, b] = place.split(",");
  const lat = Number(a), lon = Number(b);
  return Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : null;
};

/** Split a stored tag string for display.
 *
 * 🔴 TAKES `string | undefined`. `tags` was added by migration on 2026-08-11, and while the native
 * schema backfills it (`ADD COLUMN tags TEXT NOT NULL DEFAULT ''`, db.rs) the WEB store returns rows
 * exactly as stored and does not default on read — so a row written before that date, or restored
 * from a pre-2026-08-11 backup, arrives with `tags === undefined`. This function then threw
 * `Cannot read properties of undefined (reading 'split')` inside `allTags`, which runs in a
 * `useMemo` at the top of <Dashboard> — so ONE such entry took down the ENTIRE screen to the
 * ErrorBoundary ("Something went wrong"), not just the tag row. App.tsx already guarded with
 * `e.tags ?? ""` at all three of its call sites; `allTags` was the one that was missed. Guarding the
 * shared primitive fixes every caller at once instead of the one that happened to be found. */
export const tagList = (tags: string | null | undefined): string[] =>
  String(tags ?? "").split(",").map((t) => t.trim()).filter(Boolean);

/** Every tag in use, most-used first — powers the tag filter. */
export function allTags(entries: JournalEntry[]): { tag: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const e of entries) for (const t of tagList(e.tags)) counts.set(t, (counts.get(t) ?? 0) + 1);
  return [...counts.entries()].map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));
}

// Metadata-only shape sent to the AI brain — NO body. Matches the server contract:
//   POST /api/apps/solo_journal/reflect  { entries: [{ title, mood, timestamp }] }
export interface EntryMeta {
  title: string;
  mood: number;
  timestamp: string;
}

// ── DERIVED SIGNALS (metadata-only) ──────────────────────────────────────────
// A self-describing aggregate computed ON-DEVICE in Rust from entry METADATA only.
// This is what the AI brain reflects on now — NOT the raw entry list, NEVER the body.
// `length` stats are char COUNTS of the body, not the body text. Keywords are from
// TITLES only. Mirrors WeekSignals in src-tauri/src/db.rs.
export type FactTone = "neutral" | "up" | "down";

// A pre-ranked, ready-to-say observational fact computed on-device (Rust). Each `text` is a
// complete sentence already carrying the figure + unit; the reflection is grounded in these.
export interface HeadlineFact {
  kind: "cadence" | "mood" | "trend" | "rhythm" | "extreme" | "keyword" | "length" | "shift";
  text: string;
  tone: FactTone;
}

export interface WeekSignals {
  schema: string;
  window_days: number;
  entry_count: number;
  active_days: number;
  mood: {
    mean: number;
    median: number;
    variance: number;
    min: number;
    max: number;
    distribution: Record<string, number>; // "1".."5" -> count
  };
  by_weekday: { weekday: string; entries: number; mean_mood: number }[];
  title_keywords: { word: string; count: number }[];
  cadence: {
    current_streak: number;
    longest_run: number;
    gap_days: number;
    entries_per_active_day: number;
  };
  length: {
    mean_chars: number;
    median_chars: number;
    min_chars: number;
    max_chars: number;
  };
  // Last-7-days slice vs the rest of the window (recent-vs-own-baseline deltas).
  recent: {
    recent_entries: number;
    baseline_entries: number;
    recent_mean_mood: number;
    baseline_mean_mood: number;
    mood_delta: number;
    mood_direction: "up" | "down" | "flat";
    recent_per_week: number;
    baseline_per_week: number;
    cadence_direction: "more" | "less" | "same";
  };
  extremes: {
    highest_mood: number;
    highest_weekday: string;
    lowest_mood: number;
    lowest_weekday: string;
    mood_range: number;
  };
  // Pre-ranked, ready-to-say observational facts the reflection is grounded in.
  headline_facts: HeadlineFact[];
}

export const store = {
  listEntries: (limit?: number) => invoke<JournalEntry[]>("list_entries", { limit: limit ?? 50000 }),
  /** Every word must appear in the title or body. Runs on the device — the query never leaves it. */
  searchEntries: (query: string, limit?: number) =>
    invoke<JournalEntry[]>("search_entries", { query, limit: limit ?? 50000 }),
  getEntry: (id: string) => invoke<JournalEntry | null>("get_entry", { id }),
  createEntry: (input: NewEntry) => invoke<JournalEntry>("create_entry", { input }),
  updateEntry: (id: string, patch: EntryPatch) => invoke<void>("update_entry", { id, patch }),
  deleteEntry: (id: string) => invoke<void>("delete_entry", { id }),
  // Returns the past-week metadata (title + mood + timestamp) — used to feed the brain.
  weekMeta: () => invoke<EntryMeta[]>("week_meta"),
  // Returns the past-window DERIVED SIGNALS aggregate — the richer object the brain
  // reflects on. Computed on-device; body text never leaves (length = char counts only).
  weekSignals: (windowDays = 28) => invoke<WeekSignals>("week_signals", { windowDays }),
};

// Group entries by "MMMM yyyy" month bucket, newest first — mirrors the iOS TimelineView.
interface MonthBucket {
  label: string;
  sortKey: number;
  items: JournalEntry[];
}


/** One day in the calendar: the entry written that day, if any. */
export interface CalendarDay {
  /** "YYYY-MM-DD" in LOCAL time — the day the writer experienced, not UTC. */
  date: string;
  day: number;
  /** null on the blank cells that pad the first week. */
  entry: JournalEntry | null;
}

export interface CalendarMonth {
  label: string;
  sortKey: number;
  /** Leading blanks + every day of the month, so a 7-column grid lines up under Mon–Sun. */
  cells: (CalendarDay | null)[];
  written: number;
}

const localDayKey = (iso: string): string => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * The months you have written in, as calendar grids — newest month first.
 *
 * Only months with at least one entry appear: an empty grid is noise, and a journal that has run for
 * years should not make you scroll through the months you missed. Weeks start Monday.
 * If a day holds several entries the first (newest, as the list arrives) represents it.
 */
export function buildCalendar(entries: JournalEntry[]): CalendarMonth[] {
  const byDay = new Map<string, JournalEntry>();
  for (const e of entries) {
    const k = localDayKey(e.created_at);
    if (!byDay.has(k)) byDay.set(k, e);
  }
  const months = new Map<string, CalendarMonth>();
  for (const e of entries) {
    const d = new Date(e.created_at);
    const y = d.getFullYear(), m = d.getMonth();
    const key = `${y}-${m}`;
    if (months.has(key)) continue;

    const first = new Date(y, m, 1);
    // getDay(): Sunday=0. Shift so Monday=0 and the grid reads Mon–Sun.
    const lead = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const cells: (CalendarDay | null)[] = Array(lead).fill(null);
    let written = 0;
    for (let day = 1; day <= daysInMonth; day++) {
      const date = `${y}-${String(m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const entry = byDay.get(date) ?? null;
      if (entry) written++;
      cells.push({ date, day, entry });
    }
    months.set(key, {
      label: first.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
      sortKey: y * 12 + m,
      cells,
      written,
    });
  }
  return [...months.values()].sort((a, b) => b.sortKey - a.sortKey);
}


/** One month on the trend strip. */
export interface TrendMonth {
  label: string;      // "Aug"
  full: string;       // "August 2026"
  sortKey: number;
  entries: number;
  meanMood: number;   // 0 when nothing was written
  days: number;       // distinct days written in
}

export interface Trends {
  months: TrendMonth[];      // oldest → newest, so the strip reads left to right
  totalEntries: number;
  totalDays: number;
  meanMood: number;
  /** Which weekday you write on most — "Sunday", or null if there isn't enough to say. */
  busiestWeekday: string | null;
  /** Mean mood of the last 30 days vs the 30 before it. null when there is no baseline. */
  moodDelta: number | null;
}

const WEEKDAYS = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

/**
 * Your own patterns over time — the thing Daylio's entire product is, and Stoic and Reflectly both
 * sell. Computed on the device from entries we already store: no AI call, nothing sent anywhere.
 *
 * Deliberately descriptive, never advisory: it reports what you did, it does not tell you what it
 * means or what to do about it (the app's observational fence).
 */
export function buildTrends(entries: JournalEntry[], monthsBack = 12): Trends {
  const now = new Date();
  const months: TrendMonth[] = [];
  for (let i = monthsBack - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      label: d.toLocaleDateString("en-US", { month: "short" }),
      full: d.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
      sortKey: d.getFullYear() * 12 + d.getMonth(),
      entries: 0, meanMood: 0, days: 0,
    });
  }
  const byKey = new Map(months.map((m) => [m.sortKey, m]));
  const moodSum = new Map<number, number>();
  const dayKeys = new Map<number, Set<string>>();
  const weekdayCount = new Array(7).fill(0);

  let total = 0, moodTotal = 0;
  const allDays = new Set<string>();
  const DAY = 86_400_000;
  let recentSum = 0, recentN = 0, baseSum = 0, baseN = 0;

  for (const e of entries) {
    const d = new Date(e.created_at);
    if (Number.isNaN(d.getTime())) continue;
    total++; moodTotal += e.mood;
    weekdayCount[d.getDay()]++;
    allDays.add(localDayKey(e.created_at));

    const age = now.getTime() - d.getTime();
    if (age <= 30 * DAY) { recentSum += e.mood; recentN++; }
    else if (age <= 60 * DAY) { baseSum += e.mood; baseN++; }

    const k = d.getFullYear() * 12 + d.getMonth();
    const m = byKey.get(k);
    if (!m) continue;
    m.entries++;
    moodSum.set(k, (moodSum.get(k) ?? 0) + e.mood);
    let set = dayKeys.get(k);
    if (!set) { set = new Set(); dayKeys.set(k, set); }
    set.add(localDayKey(e.created_at));
  }
  for (const m of months) {
    if (m.entries > 0) m.meanMood = (moodSum.get(m.sortKey) ?? 0) / m.entries;
    m.days = dayKeys.get(m.sortKey)?.size ?? 0;
  }

  const maxWeekday = Math.max(...weekdayCount);
  // Only name a weekday when it is genuinely ahead — otherwise it is noise dressed as a finding.
  const busiest = maxWeekday >= 3 && weekdayCount.filter((c) => c === maxWeekday).length === 1
    ? WEEKDAYS[weekdayCount.indexOf(maxWeekday)]
    : null;

  return {
    months,
    totalEntries: total,
    totalDays: allDays.size,
    meanMood: total ? moodTotal / total : 0,
    busiestWeekday: busiest,
    moodDelta: recentN >= 3 && baseN >= 3 ? recentSum / recentN - baseSum / baseN : null,
  };
}

export function groupByMonth(entries: JournalEntry[]): { label: string; items: JournalEntry[] }[] {
  const buckets = new Map<string, MonthBucket>();
  for (const e of entries) {
    const d = new Date(e.created_at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = {
        label: d.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
        sortKey: d.getFullYear() * 12 + d.getMonth(),
        items: [],
      };
      buckets.set(key, bucket);
    }
    bucket.items.push(e);
  }
  return [...buckets.values()]
    .sort((a, b) => b.sortKey - a.sortKey)
    .map((b) => ({ label: b.label, items: b.items }));
}
